import type { PayloadAction } from '@reduxjs/toolkit';
import { combineReducers, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import type { JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import storage from '../storage';

import { actionOnMessage } from '@skynet/data-ws-mm';

import type {
  JsonAddLoanSuccess,
  JsonLoan,
  JsonLoansSuccess,
  LoansState,
} from 'src/store/loans/types';
import { LoanCommands } from '@skynet/loans';
import {
  LoanOverviewCommands,
  LoanTypes,
  loanOverviewReducer,
  loanOverviewActions,
  initialState as LoanOverviewInitialState,
} from '@skynet/data-loan-overview';
import { startAppListening } from 'src/store/middleware/listenerMiddleware';
import { balancesActions } from 'src/store/shared/balances/slice';
import { isSubAccountsSectionFrame } from 'src/store/shared/balances/type-guards';

import { skyRouter } from 'src/routes/sky-router';

import type { AddLoanPayload, UpdateLoanPayload } from '@skynet/data-loan-terms';
import { tasksActions } from 'src/store/shared/tasks/slice';
import loansKpiReducer from 'src/store/loans/kpi/slice';
import { hideLoan, listen } from './actions';

import loansBalancesUIReducer, {
  balanceTableOptsActions as loansBalanceTableOptsActions,
} from './balance-table-opts/slice';
import loansDepthViewOptionsReducer from './depth-view-opts/slice';
import loansFundsReducer, {
  initialState as LoansFundsInitialState,
  loanFundsActions,
} from './funds/slice';
import {
  loanAmountsReducer as loansAmountsReducer,
  initialState as LoansAmountsInitialState,
} from '@skynet/data-loan-amounts';
import loansSupportedTokensReducer, {
  initialState as LoansSupportedTokensInitialState,
} from './supported-tokens/slice';
import loansProfitReducer, { initialState as LoansProfitInitialState } from './profit/slice';
import {
  exchangeLiquidityReducer as loansExchangesReducer,
  initialState as LoansExchangesInitialState,
  exchangeLiquidityActions as loanExchangesActions,
} from '@skynet/data-loan-exchanges';
import loansLiquidityReducer, {
  initialState as LoansLiquidityInitialState,
} from './liquidity/slice';
import loansTradingViewReducer, {
  initialState as LoansTradingViewState,
} from './trading-view/slice';
import loansSummaryReducer, { initialState as LoansSummaryInitialState } from './summary/slice';
import {
  strikePricesReducer as loansStrikePricesReducer,
  initialState as LoansStrikePricesInitialState,
} from '@skynet/data-strike-prices';
import {
  loanTermsReducer,
  initialState as LoanTermsInitialState,
  loanTermsActions,
} from '@skynet/data-loan-terms';
import { startCoinGeckoLinkRefreshListening } from 'src/store/shared/coingecko-link-refresh';
import { loansArchiveReducer, archiveInitialState } from '@skynet/data-archive';
import loansBotReducer, {
  loansBotActions,
  initialState as LoansBotInitialState,
} from './bots/slice';
import loanAccountThresholdReducer from './threshold/slice';
import { EMPTY_ARRAY } from 'src/constants';
import {
  selectCurrentLoan,
  selectDefaultLoan,
  selectIsLoanExist,
  selectLoansList,
  selectTargetLoan,
} from 'src/store/loans/selectors';
import { resetStore, withResetState } from 'src/store/actions';
import { selectExchanges } from 'src/store/shared/exchange-list/selectors';
import { ExchangeCommands } from '@skynet/data-shared-exchanges';
import { isApplicationDomain, isNoIdSection } from 'src/hooks/useSection';
import { routerHistoryChange } from 'src/store/router/actions';
import { ApplicationDomain } from 'src/routes/types';

const allowedCommands = [
  ...Object.values(LoanCommands),
  LoanOverviewCommands.LoanOverview.valueOf(),
];
const NEW_LOAN_ID_PLACEHOLDER = 9999999999999;

registerErrorMessage(LoanCommands.UpdateLoan, {
  title: 'Loan Terms Update Error',
  getDescription: (json: JsonFailure) =>
    `Sorry, the error has occurred on updating loan terms. ${json.message}`,
});

registerErrorMessage(LoanCommands.Loans, {
  title: 'Loans Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

// `GetReport` is owned by store/shared/reports; its error registration lives there.

registerErrorMessage(LoanCommands.AddLoan, {
  title: 'Loans Error',
  getDescription: (json: JsonFailure) =>
    `Sorry, the error has occurred on creating a new loan. ${json.message}`,
});

registerErrorMessage(LoanCommands.HideLoan, {
  title: 'Archive Loan Error',
  getDescription: (json: JsonFailure) => String(json.message),
});

// `ShowLoan` (restore) is registered in @skynet/data-archive
// (packages/data/archive/src/slices.ts), next to the only slice that reads its
// failure.

const initialState: LoansState = {
  error: null,
  listenStatus: null,

  // Loans, data on Login
  list: null,
  companies: EMPTY_ARRAY,

  // Overview, data on Listen/Overview
  isOverviewLoading: false,

  // Loan ApiKeyModals
  isNewLoanModalOpen: false, // to handle ApiKeyModals window
  resNewLoan: null, // to handle new loan creation status

  resHideLoan: null, // to handle loan terms updating status

  // Loans (right-hand) sidebar and header
  isSidebarOpen: false,
  targetLoan: '',
  targetExchange: 'overview',

  overviewActiveTab: '',

  // sortkey
  sortKey: 'created_asc',
  searchInput: '',
  loanTypeFilter: null,
  hideEmptyAccounts: false,
};

const loansSlice = createSlice({
  name: 'loans',
  initialState,
  reducers: {
    toggleSidebar(state) {
      state.isSidebarOpen = !state.isSidebarOpen;
    },

    setTargetLoan(state, action: PayloadAction<{ loanId: string }>) {
      state.targetLoan = action.payload.loanId;
    },

    setTargetExchange(state, action: PayloadAction<{ exchangeId: string }>) {
      state.targetExchange = action.payload.exchangeId || 'overview';
    },

    setTarget(state, action: PayloadAction<{ loanId: string; exchangeId?: string }>) {
      state.listenStatus = null;
      state.targetLoan = action.payload.loanId;
      state.targetExchange = action.payload.exchangeId ?? 'overview';
      state.resNewLoan = null;
    },

    openNewLoanModal(state) {
      state.isNewLoanModalOpen = true;
      state.resNewLoan = null;
    },
    closeNewLoanModal(state, action: { payload: object }) {
      console.debug('closeNewLoanModal action:', action);
      state.isNewLoanModalOpen = false;
    },

    setOverviewActiveTab(state, action: { payload: string }) {
      state.overviewActiveTab = action.payload;
    },

    updateNewLoanId(state, action: { payload: { loanId: number } }) {
      const { loanId } = action.payload;
      state.list?.forEach((loan) => {
        if (loan.id === NEW_LOAN_ID_PLACEHOLDER) {
          loan.id = loanId;
        }
      });
    },
    setSearchInput(state, action: PayloadAction<string>) {
      state.searchInput = action.payload;
    },
    setSortKey(state, action: PayloadAction<string>) {
      state.sortKey = action.payload;
    },
    setLoanTypeFilter(state, action: PayloadAction<number[] | null>) {
      state.loanTypeFilter = action.payload;
    },
    setHideEmptyAccounts(state, action: PayloadAction<boolean>) {
      state.hideEmptyAccounts = action.payload;
    },

    setList(state, action: { payload: JsonLoan[] }) {
      state.list = action.payload;
    },
    // clearArchivedLoans(state) {
    //   state.archivedLoans = [];
    // },
  },
  extraReducers: (builder) => {
    // addLoan

    builder.addCase(loanTermsActions.addLoan.pending, (state, action) => {
      console.log('loans/addLoan.pending:', action);
      state.resNewLoan = 'pending';
    });

    builder.addCase(loanTermsActions.addLoan.rejected, (state, action) => {
      console.log('loans/addLoan.rejected:', action);
      state.resNewLoan = 'error';
    });

    builder.addCase(loanTermsActions.addLoan.fulfilled, (state, action) => {
      console.debug('loans/addLoan.fulfilled', action.payload);
      // state.isNewLoanSubmitting = false;

      const addedData: AddLoanPayload = action.payload;

      state.list?.push({
        id: NEW_LOAN_ID_PLACEHOLDER,
        name: addedData.name,
        activeAccountsCount: 0,
        LoanType: addedData.loanType ?? LoanTypes.LO,
      });

      // A brand new loan has no accounts yet, so the "Hide empty projects"
      // filter would swallow it right after creation. Turn the filter off so
      // the loan the user just created stays visible in the sidebar.
      state.hideEmptyAccounts = false;
    });

    // hideLoan

    builder.addCase(hideLoan.pending, (state, action) => {
      console.log('loans/hideLoan.pending:', action);
      state.resHideLoan = 'pending';
    });

    // listen

    builder.addCase(listen, (state, action) => {
      const { section } = action.payload;
      const { isInitCall } = action.meta;
      const isOverview = ['overview', 'terms'].includes(section);
      if (isInitCall) {
        state.listenStatus = 'pending';
        state.isOverviewLoading = isOverview;
      }
    });

    // on WS message

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      console.debug(`loans/processing ${actionOnMessage.toString()}`, action, allowedCommands);

      const { command, okCommand } = json;

      if (error) {
        state.error = error;

        switch (errorCommand) {
          case LoanCommands.HideLoan:
            state.resHideLoan = 'error';
            break;
          case LoanCommands.AddLoan:
            state.resNewLoan = 'error';
            break;
          case undefined:
          default:
        }

        return;
      }

      if (isOkCommand(command) && okCommand === LoanCommands.HideLoan) {
        const { result: loanID }: JsonAddLoanSuccess = json;
        state.targetLoan = '';
        state.resHideLoan = 'success';
        console.debug('HideLoan loanID:', loanID);
        return;
      }

      if (isOkCommand(command) && okCommand === LoanCommands.AddLoan) {
        const { result: loanID }: JsonAddLoanSuccess = json;
        state.resNewLoan = 'success';
        state.isNewLoanModalOpen = false;
        console.debug('AddLoan loanID:', loanID);
        return;
      }

      if (command === LoanCommands.Loans) {
        const { list, companies }: JsonLoansSuccess = json;

        state.listenStatus = 'success';
        state.list = list;
        state.companies = companies;
        state.error = null;
        return;
      }

      // Overview
      if (command === LoanOverviewCommands.LoanOverview) {
        state.isOverviewLoading = false;
        return;
      }

      // Balances
      if (command === LoanCommands.Balances && !isSubAccountsSectionFrame(json)) {
        state.listenStatus = 'success';
      }

      // Exchange details
    });

    builder.addCase(resetStore, (_, action) => {
      console.log('~ action', action);
      return initialState;
    });
  },
});

export const loansActions = {
  ...loansSlice.actions,
  hideLoan,
  listen,
};

// export const { toggleSidebar, openNewLoanModal, closeNewLoanModal } = loansSlice.actions

const balanceTableOptsConfig = {
  key: 'loans/balanceTableOpts',
  version: 1,
  storage,
  blacklist: ['viewMode'],
};

const persistedLoansBalancesUIReducer = persistReducer(
  balanceTableOptsConfig,
  loansBalancesUIReducer,
);

const depthViewOptionsConfig = {
  key: 'loans/depthViewOptions',
  version: 1,
  storage,
};

const persistedLoansDepthViewOptionsReducer = persistReducer(
  depthViewOptionsConfig,
  loansDepthViewOptionsReducer,
);

const loansReducerConfig = {
  key: 'loans/sidebar',
  version: 1,
  storage,
  whitelist: ['sortKey', 'loanTypeFilter', 'hideEmptyAccounts'],
};

const persistedLoans = persistReducer(
  loansReducerConfig,
  withResetState(loansSlice.reducer, initialState),
);

export default combineReducers({
  index: persistedLoans,
  balancesUI: persistedLoansBalancesUIReducer,
  depthViewOptions: persistedLoansDepthViewOptionsReducer,
  funds: withResetState(loansFundsReducer, LoansFundsInitialState),
  amounts: withResetState(loansAmountsReducer, LoansAmountsInitialState),
  supportedTokens: withResetState(loansSupportedTokensReducer, LoansSupportedTokensInitialState),
  profit: withResetState(loansProfitReducer, LoansProfitInitialState),
  exchanges: withResetState(loansExchangesReducer, LoansExchangesInitialState),
  liquidity: withResetState(loansLiquidityReducer, LoansLiquidityInitialState),
  tradingView: withResetState(loansTradingViewReducer, LoansTradingViewState),
  strikePrices: withResetState(loansStrikePricesReducer, LoansStrikePricesInitialState),
  kpi: loansKpiReducer,
  summary: withResetState(loansSummaryReducer, LoansSummaryInitialState),
  terms: withResetState(loanTermsReducer, LoanTermsInitialState),
  threshold: loanAccountThresholdReducer,
  overview: withResetState(loanOverviewReducer, LoanOverviewInitialState),
  archive: withResetState(loansArchiveReducer, archiveInitialState),
  bot: withResetState(loansBotReducer, LoansBotInitialState),
});

// on switch the target loan
// it should reset balances list
startAppListening({
  actionCreator: loansActions.setTarget,
  effect: (action, listenerApi) => {
    listenerApi.dispatch(loanExchangesActions.resetState());
    listenerApi.dispatch(loanExchangesActions.loadCache(action.payload));
    listenerApi.dispatch(loanOverviewActions.resetLoanOverview());
    listenerApi.dispatch(balancesActions.resetList());
    listenerApi.dispatch(loansBotActions.resetSession());

    listenerApi.dispatch(tasksActions.resetSearchValue());
    listenerApi.dispatch(loanTermsActions.resetUpdateLoan());
    listenerApi.dispatch(loansBalanceTableOptsActions.resetIgnoreTokens());
  },
});

// mark exchange liquidity as loading when the app starts listening for the
// overview/liquidity sections. This reactivity previously lived in the slice's
// `listen` extraReducer; the slice now lives in `@skynet/data-loan-exchanges`, which
// cannot import the app-level `listen` action, so it is dispatched here instead.
startAppListening({
  actionCreator: listen,
  effect: (action, listenerApi) => {
    const { section } = action.payload;
    const { isInitCall } = action.meta;
    if (isInitCall && (section === 'overview' || section === 'liquidity')) {
      listenerApi.dispatch(loanExchangesActions.setRequestPending());
    }
  },
});

// on loan terms update, sync the loan name and type in the sidebar list
startAppListening({
  actionCreator: loanTermsActions.updateLoan.fulfilled,
  effect: (action, listenerApi) => {
    const state = listenerApi.getState();
    const updatedData: UpdateLoanPayload = action.payload;

    const newName = updatedData?.name;
    const newLoanType = updatedData?.loanType;

    // The sidebar renders the loans list, so sync against it directly so edits
    // are reflected without a page reload.
    const { list } = state.loans.index;
    if (!list) {
      return;
    }

    // Update name and/or loan type in the list for sidebar display and filtering.
    // Only real changes are applied: name is guarded against empty values so the
    // sidebar label is never blanked, and the type is synced so loanType filters
    // reflect the edit without a page reload.
    let changed = false;
    const updatedList = list.map((loan) => {
      if (loan.id !== updatedData.loanID) {
        return loan;
      }
      const next = { ...loan };
      if (newName && loan.name !== newName) {
        next.name = newName;
        changed = true;
      }
      if (newLoanType !== undefined && loan.LoanType !== newLoanType) {
        next.LoanType = newLoanType;
        changed = true;
      }
      return next;
    });

    if (changed) {
      listenerApi.dispatch(loansActions.setList(updatedList));
    }
  },
});

// Bring the CoinGecko link back after an UpdateLoan that changed the project.
startCoinGeckoLinkRefreshListening({
  updateCommand: LoanCommands.UpdateLoan,
  selectTerms: (state) => state.loans.terms,
  isTermsPageOpen: (loanID) =>
    skyRouter.getDomain() === ApplicationDomain.Loans &&
    skyRouter.getSection() === 'terms' &&
    skyRouter.getEntityId() === String(loanID),
  listen: (loanID) => loansActions.listen({ section: 'terms', loanID }, { isInitCall: false }),
});

// on new loan or loan update
// it should update committed funds and strike prices
// TODO: verify it works as before refactoring to parseResponse
// TODO: Create actions for update state of data list in new loan
startAppListening({
  actionCreator: actionOnMessage,
  effect: async (action, listenerApi) => {
    try {
      const allowedCommands = [LoanCommands.AddLoan];
      const { json, skipProcessing, okCommand } = parseResponse<JsonAddLoanSuccess>(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) {
        return;
      }

      // const json = JSON.parse((action.payload as AccountView).message) as JsonAddLoanSuccess;
      if (okCommand === LoanCommands.AddLoan.toString()) {
        const { result: loanID } = json;
        const state = listenerApi.getState();

        const {
          loans: {
            funds: { initialCommittedFunds },
            // index: { formStrikePrices, formKpi },
          },
        } = state;

        listenerApi.dispatch(loansActions.updateNewLoanId({ loanId: +loanID }));

        if (initialCommittedFunds) {
          listenerApi.dispatch(
            loanFundsActions.addCommittedFunds({
              loanID,
              amount: initialCommittedFunds,
              token: 'USD',
            }),
          );
        }

        // redirect to the loan terms
        await skyRouter.router?.navigate(`/loans/terms/${loanID}`);
      }
    } catch (error) {
      console.error('Error on processing new loan:', error);
    }
  },
});

/**
 * Listen to the WS messages and applies redirect logic to loans
 * when all the required top level (domain) data is loaded.
 */
startAppListening({
  matcher: isAnyOf(actionOnMessage, loansActions.setList),
  effect: async (action, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();

    if (!domain || !section || domain !== ApplicationDomain.Loans) {
      return;
    }

    // `setList` dispatches already carry the full updated list in Redux state,
    // so redirect evaluation runs immediately after a list update.
    // For raw `actionOnMessage` payloads, gate to Loans/Exchanges commands only:
    // parseResponse filters to the two allowed commands, but 'ok'/'error' responses
    // to those commands also pass through — the explicit command check below drops them.
    if (actionOnMessage.match(action)) {
      const { json, skipProcessing } = parseResponse(action.payload, [
        LoanCommands.Loans,
        ExchangeCommands.Exchanges,
      ]);
      if (skipProcessing || !json) {
        return;
      }
      const { command } = json;
      if (command !== LoanCommands.Loans && command !== ExchangeCommands.Exchanges) {
        return;
      }
    }

    const state = listenerApi.getState();

    const hasLoans = selectLoansList(state) !== null;
    const hasExchanges = selectExchanges(state).length > 0;
    const entityId = skyRouter.getEntityId();

    if (hasLoans && hasExchanges) {
      const defaultLoan = selectDefaultLoan(state);
      // If current section doesn't require entityId, we skip the following checks
      if (!section || !isApplicationDomain(domain) || isNoIdSection(domain, section)) {
        if (defaultLoan) {
          // we set target loan as default loan to have the entity ID in the navigation links like /loans/overview/:id
          listenerApi.dispatch(loansActions.setTargetLoan({ loanId: String(defaultLoan.id) }));
        }
        return;
      }

      // If the section requires entityId, and the entityId is not present in the URL,
      // we redirect to the default project
      if (!entityId) {
        if (defaultLoan) {
          await skyRouter.router?.navigate(`/${domain.toLowerCase()}/${section}/${defaultLoan.id}`);
        }
        return;
      }

      const hasAccess = selectIsLoanExist(entityId)(state);
      if (!hasAccess) {
        if (defaultLoan) {
          // if the default loan is not set, then all the navigation links has no entity param (/loans/overview)
          listenerApi.dispatch(loansActions.setTarget({ loanId: String(defaultLoan.id) }));
        }
        await skyRouter.router?.navigate(`/${domain.toLowerCase()}/not-found`);
        return;
      }
    }
    const loanId = selectTargetLoan(state);
    if (entityId && !loanId) {
      listenerApi.dispatch(loansActions.setTargetLoan({ loanId: entityId }));
    }
  },
});

// Listen to router history change for Loans domain.
// if URL loan ID is different from the current loan ID,
// and user has access to the URL loan, set the URL loan ID as the current one.
startAppListening({
  actionCreator: routerHistoryChange,
  effect: (action, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();
    const entityId = skyRouter.getEntityId();

    if (domain !== ApplicationDomain.Loans || !section || !entityId) {
      return;
    }

    const state = listenerApi.getState();
    const currentTarget = selectCurrentLoan(state);

    if (String(currentTarget?.id) !== entityId && selectIsLoanExist(entityId)(state)) {
      listenerApi.dispatch(loansActions.setTarget({ loanId: String(entityId) }));
    }
  },
});

startAppListening({
  actionCreator: routerHistoryChange,
  effect: (_, listenerApi) => {
    const domain = skyRouter.getDomain();
    const section = skyRouter.getSection();

    // Reset terms status when user is in Loans domain but NOT on terms page
    // or when leaving Loans domain entirely
    if (domain !== ApplicationDomain.Loans || section !== 'terms') {
      listenerApi.dispatch(loanTermsActions.resetUpdateLoan());
    }
  },
});
